import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { first } from "rxjs/operators";
import { environment } from "src/environments/environment";

export interface CmiGenerateRequest {
    mediationAgreementId?: number;
    dealId?: number;
    propertyId?: string;
    expectedTemplateVersionId: number;
    values: Record<string, string | number | boolean | null>;
}

export interface CmiFieldChange {
    sourceKey: string;
    before: string | null;
    after: string | null;
    kind: "added" | "removed" | "changed";
}

export interface CmiVersionDiff {
    current: { id: string; fileName: string | null; createdAt: string };
    previous: { id: string; fileName: string | null; createdAt: string } | null;
    changes: CmiFieldChange[];
}

/** Link público do CMI, para o proprietário ler e devolver assinado. */
export interface CmiShareToken {
    id: number;
    token: string;
    expiresAt?: string | null;
    lastViewedAt?: string | null;
    viewCount?: number;
    generatedDocumentId?: string | null;
}

export interface CmiDraftRequest {
    mediationAgreementId?: number;
    dealId?: number;
    propertyId?: string;
    expectedTemplateVersionId: number;
    values: Record<string, string | number | boolean | null>;
}

export interface CmiDraftResponse {
    mediationAgreementId?: number | null;
    dealId?: number | null;
    propertyId?: string | null;
    templateVersionId: number;
    savedAt?: string | null;
    values: Record<string, string | number | boolean | null>;
}

export interface CmiFormSchemaRequest {
    mediationAgreementId?: number;
    dealId?: number;
    propertyId?: string;
    entityId?: number;
}

export interface CmiFormSchemaOption {
    value: string | number | boolean;
    labelKey: string;
    label?: string;
    displayValue?: string;
}

export interface CmiFormSchemaField {
    sourceKey: string;
    labelKey: string;
    valueType: "TEXT" | "DATE" | "CURRENCY" | "PERCENTAGE" | "CHECKBOX";
    supportsMultiline: boolean;
    multiline: boolean;
    required: boolean;
    maxCharacters?: number | null;
    value: string | number | boolean | null;
    options?: CmiFormSchemaOption[];
    /** Só relevante quando o campo `driver` tem um destes valores. */
    relevantWhen?: { driver: string; values: Array<string | boolean> } | null;
}

export interface CmiFormSchemaGroup {
    key: string;
    labelKey: string;
    fields: CmiFormSchemaField[];
}

export interface CmiFormSchemaResponse {
    mediationAgreementId?: number | null;
    templateVersionId: number;
    resolvedScope: string;
    /** 'STANDARD' (o modelo genérico) ou 'EXCLUSIVE' (contrato de exclusividade). */
    resolvedVariant?: string | null;
    groups: CmiFormSchemaGroup[];
}

export interface CmiGeneratedDocumentSummary {
    id: string;
    fileName?: string | null;
    size?: number | null;
    templateVersionId?: number | null;
    createdAt?: string | null;
    /** Preenchido quando o proprietário devolveu esta versão assinada. */
    signedAt?: string | null;
    signedUploadedBy?: string | null;
    signedDealFileId?: number | null;
}

/** Diz se existe template publicado para este contexto e o que já foi gerado. */
export interface CmiAvailabilityResponse {
    available: boolean;
    entityId: number;
    storeId?: number | null;
    mediationAgreementId?: number | null;
    propertyId?: string | null;
    dealId?: number | null;
    templateVersionId?: number | null;
    resolvedScope?: string | null;
    resolvedVariant?: string | null;
    documents: CmiGeneratedDocumentSummary[];
}

@Injectable({ providedIn: 'root' })
export class FilesService {

    constructor(
        private http: HttpClient
    ) { }

    createMediationAgreement(data: any) {
        return this.http.post(`${environment.apiUrl}/mediation-agreement`, data, { withCredentials: true }).pipe(first());
    }

    updateMediationAgreement(data: any, dealId: any) {
        return this.http.patch(`${environment.apiUrl}/mediation-agreement/${dealId}`, data, { withCredentials: true }).pipe(first());
    }

    getMediationAgreement(dealId: any) {
        return this.http.get(`${environment.apiUrl}/mediation-agreement/${dealId}`, { withCredentials: true }).pipe(first());
    }

    generateCmiDocument(data: CmiGenerateRequest) {
        return this.http.post(`${environment.apiUrl}/mediation-agreement/cmi/generate`, data, {
            withCredentials: true,
            observe: "response",
            responseType: "blob",
        }).pipe(first());
    }

    getCmiAvailability(data: CmiFormSchemaRequest) {
        return this.http.post<CmiAvailabilityResponse>(
            `${environment.apiUrl}/mediation-agreement/cmi/availability`,
            data,
            { withCredentials: true },
        ).pipe(first());
    }

    /** Só diz se a entidade tem CMI publicado — não precisa de imóvel gravado. */
    getCmiTemplateAvailability(storeId?: number) {
        let params = new HttpParams();
        if (storeId) params = params.set("storeId", String(storeId));

        return this.http.get<{ available: boolean }>(
            `${environment.apiUrl}/mediation-agreement/cmi/template-availability`,
            { withCredentials: true, params },
        ).pipe(first());
    }

    getCmiFormSchema(data: CmiFormSchemaRequest) {
        return this.http.post<CmiFormSchemaResponse>(
            `${environment.apiUrl}/mediation-agreement/cmi/form-schema`,
            data,
            { withCredentials: true },
        ).pipe(first());
    }

    /**
     * Grava o preenchimento em curso sem gerar o PDF. Ao contrário do
     * `generateCmiDocument`, não exige os campos obrigatórios — é o que permite
     * sair a meio do CMI sem perder o que já lá está.
     */
    saveCmiDraft(data: CmiDraftRequest) {
        return this.http.post<CmiDraftResponse>(
            `${environment.apiUrl}/mediation-agreement/cmi/draft`,
            data,
            { withCredentials: true },
        ).pipe(first());
    }

    /** O que mudou entre esta versão do CMI e a anterior. */
    getCmiVersionDiff(documentId: string, previous?: string) {
        let params = new HttpParams();
        if (previous) params = params.set("previous", previous);

        return this.http.get<CmiVersionDiff>(
            `${environment.apiUrl}/mediation-agreement/cmi/documents/${documentId}/diff`,
            { params, withCredentials: true },
        ).pipe(first());
    }

    /** Link activo de partilha do CMI com o proprietário, se existir. */
    getCmiShare(dealId: number) {
        return this.http.get<CmiShareToken | null>(
            `${environment.apiUrl}/mediation-agreement/cmi/deals/${dealId}/share`,
            { withCredentials: true },
        ).pipe(first());
    }

    createCmiShare(dealId: number, data: { days?: number; generatedDocumentId?: string } = {}) {
        return this.http.post<CmiShareToken>(
            `${environment.apiUrl}/mediation-agreement/cmi/deals/${dealId}/share`,
            data,
            { withCredentials: true },
        ).pipe(first());
    }

    revokeCmiShare(dealId: number) {
        return this.http.delete<{ success: boolean }>(
            `${environment.apiUrl}/mediation-agreement/cmi/deals/${dealId}/share`,
            { withCredentials: true },
        ).pipe(first());
    }

    /** Pré-visualização em PDF, sem gravar documento nem consumir versão. */
    previewCmiDocument(data: CmiGenerateRequest) {
        return this.http.post(`${environment.apiUrl}/mediation-agreement/cmi/preview`, data, {
            withCredentials: true,
            observe: "response",
            responseType: "blob",
        }).pipe(first());
    }

    downloadGeneratedCmiDocument(documentId: string) {
        return this.http.get(`${environment.apiUrl}/mediation-agreement/cmi/documents/${documentId}/download`, {
            withCredentials: true,
            observe: "response",
            responseType: "blob",
        }).pipe(first());
    }

    createVisitForm(data: any, visit?: any) {
        return this.http.post(`${environment.apiUrl}/visit-form`, data, { withCredentials: true }).pipe(first());
    }

    updateVisitForm(data: any, dealId: any) {
        return this.http.patch(`${environment.apiUrl}/visit-form/${dealId}`, data, { withCredentials: true }).pipe(first());
    }

    /**
     * Ficha de visita gerada NO SERVIDOR a partir de uma visita já marcada
     * (página nova dos negócios). Devolve `{ visitFormId, fileName }`; o PDF
     * fica nos documentos do negócio (`/deals/document/<fileName>`).
     */
    generateVisitSheet(
        visitId: number,
        overrides: {
            responsible?: string | null;
            listingAgent?: string | null;
            clientName?: string | null;
            clientContact?: string | null;
        } = {},
    ) {
        return this.http
            .post<any>(`${environment.apiUrl}/visit-form/visits/${visitId}/generate`, overrides, {
                withCredentials: true,
            })
            .pipe(first());
    }

    /**
     * Ficha de visita VIRTUAL (chat v3): emite o link por token para o cliente
     * responder e rubricar no telemóvel (ou no tablet do consultor). Devolve
     * `{ token, url, formId, answered }`.
     */
    issueVirtualVisitForm(visitId: number) {
        return this.http
            .post<any>(`${environment.apiUrl}/visit-form/visits/${visitId}/virtual`, {}, { withCredentials: true })
            .pipe(first());
    }

    /**
     * PDF de um documento do negócio como blob (mesma rota da tab Documentos),
     * para descarregar sem depender de popups: um `window.open` fora do clique
     * é bloqueado; um `<a download>` de blob não.
     */
    downloadDealDocument(fileName: string) {
        return this.http
            .get(`${environment.apiUrl}${environment.dirDealsFiles}${fileName}`, {
                responseType: 'blob',
                withCredentials: true,
            })
            .pipe(first());
    }

    getVisitForm(dealId: any, type: 'sale' | 'listing') {
        const params = new HttpParams().set('type', type);
        return this.http.get<any>(`${environment.apiUrl}/visit-form/${dealId}`, { params, withCredentials: true }).pipe(first());
    }
}
